import styled, { css, keyframes } from 'styled-components';
import { Box, Text } from '@rsos/components/capstone/base';
import {
  ACCEPTED,
  DISPATCH_REQUESTED,
  DECLINED,
  NEW,
  TIMEOUT,
  CANCELED,
} from '@rsos/constants/alertStatuses';

// used for statuses only
// includes the NEW status
const statusColors = (status, isSupplementalOnly) => {
  const statuses = {
    [ACCEPTED]: 'primaryBase',
    [DECLINED]: 'lightDanger',
    [CANCELED]: 'lightDanger',
    [DISPATCH_REQUESTED]: 'primaryBase',
    [NEW]: 'heavyWarning',
    [TIMEOUT]: 'lightDanger',
    default: 'primaryText',
  };
  return isSupplementalOnly
    ? statuses['default']
    : status === DISPATCH_REQUESTED
      ? 'heavyWarning'
      : statuses[status] || statuses['default'];
};

const blink = keyframes`
  50% {
    opacity: 20%;
  }
`;

const alertNotificationDot = css`
  content: '';
  position: absolute;
  top: 50%;
  left: 8px;
  width: 8px;
  height: 8px;
  margin-top: -4px; /* Center the 8px dot vertically */
  background-color: ${({ theme }) => theme.colors.primaryBase}; // Blue
  border-radius: 50%;
  animation: ${blink} 1s ease-in-out infinite;
`;

const statusBorder = css`
  content: '';
  position: absolute;
  overflow: hidden;
  top: 16px;
  bottom: 16px;
  left: 9px;
  width: 5px;
  border-radius: 10px;
  background-color: none;

  ${({ isCentralStation, isUnread }) =>
    !isCentralStation && isUnread && alertNotificationDot};

  ${({ isCentralStation, isUnread, status, theme }) =>
    !(!isCentralStation && isUnread) &&
    status === ACCEPTED &&
    `background-color: ${theme.colors.primaryBase}`};

  ${({ isCentralStation, isUnread, status, theme }) =>
    !(!isCentralStation && isUnread) &&
    [DECLINED, CANCELED, TIMEOUT].includes(status) &&
    `background-color: ${theme.colors.lightDanger}`};
`;

export const Container = styled(Box).attrs(({ isMapMarker, status }) => ({
  width: isMapMarker ? 210 : '100%',
  minHeight: !isMapMarker && 65,
  horizontal: true,
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: 'background',
  padding: !isMapMarker && '16px 24px',
  cursor: 'pointer',
  transition: 'background-color 0.3s',
  opacity: !isMapMarker && [DECLINED, CANCELED].includes(status) ? 0.4 : 1,
}))`
  border-bottom: ${({ isMapMarker, theme }) =>
    isMapMarker ? 'none' : `1px solid ${theme.colors.lightLineDivider}`};
  color: ${({ theme }) => theme.colors.primaryText};
  font-family: 'Roboto-Regular', sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 1.2;

  &:hover {
    background-color: ${({ theme, isMapMarker }) =>
      isMapMarker && theme.colors.background};
  }
  &:focus {
    background-color: ${({ theme, isSelected }) =>
      !isSelected && theme.colors.foreground};
    outline: none;
  }
  &:before {
    ${({ isMapMarker, isSelected, isStatusUpdated, isUnread, status }) =>
      !isMapMarker &&
      !isSelected &&
      (isStatusUpdated || isUnread) &&
      status &&
      statusBorder};
  }
  ${({ isHovered, isSelected, theme }) =>
    (isHovered || isSelected) &&
    `background-color: ${theme.colors.highlights}`};
`;

export const IconWrapper = styled(Box).attrs({
  flex: '0 0 auto',
  align: 'center',
  justify: 'center',
  marginRight: 15,
  overflow: 'hidden',
})``;

export const IconContainer = styled(Box).attrs(({ bg }) => ({
  width: 23,
  height: 23,
  bg: bg,
  borderRadius: '4px',
  align: 'center',
  justify: 'center',
}))``;

export const InfoWrapper = styled(Box).attrs({
  flex: '1 1 auto',
  ellipsis: true,
})``;

export const TimeWrapper = styled(Box).attrs({
  flex: '0 0 auto',
  horizontal: true,
  justify: 'flex-end',
  marginLeft: 10,
  overflow: 'hidden',
})``;

export const Icon = styled(Box).attrs({
  marginBottom: '3px',
  align: 'center',
  justify: 'center',
})`
  text-align: center;
`;

export const Type = styled(Text).attrs(({ isActiveAssailant }) => ({
  color: isActiveAssailant ? 'lightDanger' : 'secondaryText',
  size: 'xsmall',
  textTransform: 'uppercase',
}))``;

export const Time = styled(Text).attrs({
  color: 'secondaryText',
  size: 'xsmall',
  textAlign: 'right',
})``;

export const Name = styled(Text).attrs(({ isActiveAssailant }) => ({
  color: isActiveAssailant ? 'lightDanger' : 'primaryText',
  size: 'normal',
  ellipsis: true,
}))``;

export const Address = styled(Text).attrs(({ isActiveAssailant }) => ({
  color: isActiveAssailant ? 'lightDanger' : 'primaryText',
  size: 'normal',
  ellipsis: true,
}))``;

export const Status = styled(Text).attrs(({ status }) => ({
  color: status && statusColors(status),
  size: 'small',
  textTransform: 'uppercase',
  mt: '4px',
}))``;

export const EmergencyType = styled(Text).attrs(({ isActiveAssailant }) => ({
  color: isActiveAssailant ? 'lightDanger' : 'primaryText',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  size: 'large',
  mb: '4px',
}))``;

export const SubType = styled(Text).attrs({
  color: 'secondaryText',
  size: 'small',
  mb: '4px',
})``;
